import { createPublicViewCache, publicViewContext } from './public-view-cache.js';
import { getCachedProductSearchIndex, loadProductSearchIndex } from './product-search-index.js';
import { matchesShortcut } from './keyboard-shortcuts.js';
import './mystery-availability.js';

const SEARCH_STORAGE_KEY = 'sigma-recent-searches';
const SEARCH_HISTORY_STATE_KEY = 'sigmaHeaderSearch';
const SEARCH_HISTORY_LIMIT = 4;
const SEARCH_MINIMUM_LENGTH = 2;
const SEARCH_REQUEST_DELAY = 160;
const COMPACT_SEARCH_RESULT_LIMIT = 5;
const DESKTOP_SEARCH_RESULT_LIMIT = 4;
const SEARCH_PLACEHOLDER_INITIAL_DELAY = 2000;
const SEARCH_PLACEHOLDER_HOLD_DURATION = 4500;
const SEARCH_PLACEHOLDER_CLEAR_DELAY = 380;
const SEARCH_PLACEHOLDER_EXIT_DURATION = 220;
const SEARCH_PLACEHOLDER_ENTER_DURATION = 320;
const SEARCH_PLACEHOLDER_EXIT_EASING = 'cubic-bezier(0.64, 0, 0.78, 0)';
const SEARCH_PLACEHOLDER_ENTER_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const SEARCH_RESIZE_DURATION = 240;
const SEARCH_RESIZE_EASING = 'cubic-bezier(0.77, 0, 0.175, 1)';
// Optional roaster enrichment must not gate the usable search shell.
const loadRoasterDirectory = (url) => import('./roaster-directory-data.js')
  .then((module) => module.loadRoasterDirectory(url));
const vendorCountRequests = new Map();
const vendorCountCache = createPublicViewCache({ maxEntries: 24, maxBytes: 8192 });

const loadAvailableVendorCount = (destination) => {
  const key = `${publicViewContext()}\n${destination}`;
  const cached = vendorCountCache.get(key);
  if (cached !== undefined) return Promise.resolve(cached);
  if (!vendorCountRequests.has(key)) {
    const url = new URL(destination, window.location.origin);
    url.searchParams.set('view', 'search-vendor-count');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    vendorCountRequests.set(key, fetch(url, { headers: { Accept: 'text/html' }, signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Vendor count failed with ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (!Number.isSafeInteger(data.count) || data.count < 0) return null;
        vendorCountCache.set(key, data.count, 8);
        return data.count;
      })
      .catch(() => null)
      .finally(() => { clearTimeout(timer); vendorCountRequests.delete(key); }));
  }
  return vendorCountRequests.get(key);
};

const normalizeSearchText = (value) =>
  String(value ?? '')
    .normalize('NFKD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase()
    .replace(/[^\p{Letter}\p{Number}]+/gu, ' ')
    .trim();

const escapeSearchPattern = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightSearchMatches = (container, searchTerm) => {
  container.querySelectorAll('[data-predictive-search-match]').forEach((match) => {
    const parent = match.parentNode;
    match.replaceWith(document.createTextNode(match.textContent || ''));
    parent?.normalize();
  });

  const terms = searchTerm
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .sort((first, second) => second.length - first.length);
  if (!terms.length) return;

  const matcher = new RegExp(`(${terms.map(escapeSearchPattern).join('|')})`, 'gi');
  container
    .querySelectorAll(
      '[data-predictive-search-vendor], [data-predictive-search-title], [data-predictive-search-notes]',
    )
    .forEach((target) => {
      const text = target.textContent || '';
      const matches = Array.from(text.matchAll(matcher));
      if (!matches.length) return;

      const fragment = document.createDocumentFragment();
      let cursor = 0;
      matches.forEach((match) => {
        const index = match.index || 0;
        if (index > cursor) fragment.append(text.slice(cursor, index));

        const highlight = document.createElement('mark');
        highlight.dataset.predictiveSearchMatch = '';
        highlight.textContent = match[0];
        fragment.append(highlight);
        cursor = index + match[0].length;
      });
      if (cursor < text.length) fragment.append(text.slice(cursor));
      target.replaceChildren(fragment);
    });
};

export const normalizeRecentSearches = (values, limit = SEARCH_HISTORY_LIMIT) => {
  if (!Array.isArray(values)) return [];

  const searches = [];
  const seenSearches = new Set();

  values.forEach((value) => {
    if (typeof value !== 'string') return;

    const searchTerm = value.trim();
    const searchKey = normalizeSearchText(searchTerm);
    if (!searchTerm || !searchKey || seenSearches.has(searchKey) || searches.length >= limit) return;

    seenSearches.add(searchKey);
    searches.push(searchTerm);
  });

  return searches;
};

export const normalizeSearchValues = (value) => {
  const values = Array.isArray(value) ? value.flat(Infinity) : [value];
  return values.map(normalizeSearchText).filter(Boolean);
};

const getSearchMatchQuality = (value, searchTerm) => {
  const normalizedSearchTerm = normalizeSearchText(searchTerm);
  const values = normalizeSearchValues(value);
  if (!normalizedSearchTerm || !values.length) return Number.POSITIVE_INFINITY;

  if (values.some((candidate) => candidate === normalizedSearchTerm)) return 0;
  if (values.some((candidate) => candidate.startsWith(normalizedSearchTerm))) return 1;
  if (
    values.some((candidate) =>
      candidate.split(' ').some((word) => word.startsWith(normalizedSearchTerm)),
    )
  ) {
    return 2;
  }

  const combinedValues = values.join(' ');
  if (combinedValues.includes(normalizedSearchTerm)) return 3;

  const searchTerms = normalizedSearchTerm.split(' ');
  return searchTerms.every((term) => combinedValues.includes(term))
    ? 4
    : Number.POSITIVE_INFINITY;
};

export const getSearchMatchRank = (record, searchTerm) => {
  const searchFields = record?.search || {};
  const rankedFields = [
    searchFields.title,
    searchFields.tasteNotes,
    searchFields.varieties,
    searchFields.process,
    searchFields.other,
  ];

  for (const [tier, values] of rankedFields.entries()) {
    const quality = getSearchMatchQuality(values, searchTerm);
    if (Number.isFinite(quality)) return tier * 10 + quality;
  }

  const combinedQuality = getSearchMatchQuality(rankedFields, searchTerm);
  return Number.isFinite(combinedQuality) ? 50 + combinedQuality : Number.POSITIVE_INFINITY;
};

// Preview matches and Shopify full search use different catalogue scopes.
export const formatAllResultsLabel = (_resultCount, searchTerm) =>
  `View all search results for “${searchTerm}”`;

/*
 * HEADER SEARCH MOTION
 *
 *   0ms   backdrop and centered search modal begin entering together
 * 240ms   modal settles in place
 *
 * Close reverses the same movement over a slightly faster 180ms. The header
 * stays fixed beneath the backdrop throughout both states.
 */
const SEARCH_MOTION = Object.freeze({
  closeDuration: 180,
});
const desktopSearchViewport = window.matchMedia('(min-width: 1200px)');
const reduceSearchMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const headerSearchControllers = new Map();
const getSearchResultLimit = () =>
  desktopSearchViewport.matches ? DESKTOP_SEARCH_RESULT_LIMIT : COMPACT_SEARCH_RESULT_LIMIT;

const readRecentSearches = () => {
  try {
    const searches = JSON.parse(localStorage.getItem(SEARCH_STORAGE_KEY) || '[]');
    return normalizeRecentSearches(searches);
  } catch (error) {
    return [];
  }
};

const writeRecentSearch = (searchTerm) => {
  const normalizedTerm = searchTerm.trim();
  if (!normalizedTerm) return;

  const recentSearches = normalizeRecentSearches([normalizedTerm, ...readRecentSearches()]);

  try {
    localStorage.setItem(SEARCH_STORAGE_KEY, JSON.stringify(recentSearches));
  } catch (error) {}
};

const removeRecentSearch = (searchTerm) => {
  const normalizedTerm = searchTerm.trim().toLocaleLowerCase();
  if (!normalizedTerm) return;

  const remainingSearches = readRecentSearches().filter(
    (value) => normalizeSearchText(value) !== normalizeSearchText(normalizedTerm),
  );

  try {
    if (remainingSearches.length) {
      localStorage.setItem(SEARCH_STORAGE_KEY, JSON.stringify(remainingSearches));
    } else {
      localStorage.removeItem(SEARCH_STORAGE_KEY);
    }
  } catch (error) {}
};

const initializeHeaderSearch = (root) => {
  if (root.dataset.headerSearchReady === 'true') return;

  const desktopTrigger = root.querySelector('[data-header-search-trigger]');
  const form = root.querySelector('[data-header-search-form]');
  const headerInner = root.closest('.site-header__inner');
  const triggers = headerInner
    ? Array.from(headerInner.querySelectorAll('[data-header-search-trigger]'))
    : [];
  const compactTrigger = triggers.find((trigger) => trigger !== desktopTrigger) || desktopTrigger;
  const backdrop = root.querySelector('[data-header-search-backdrop]');
  const surface = root.querySelector('[data-header-search-surface]');
  const input = root.querySelector('[data-header-search-input]');
  const closeButton = root.querySelector('[data-header-search-close]');
  const panel = root.querySelector('[data-header-search-panel]');
  const content = root.querySelector('.site-header__search-content');
  const persistentSection = root.querySelector('[data-header-search-persistent]');
  const persistentHeading = root.querySelector('[data-header-search-persistent-heading]');
  const recentSection = root.querySelector('[data-header-search-recents]');
  const recentList = root.querySelector('[data-header-search-recent-list]');
  const historyTemplate = root.querySelector('[data-header-search-history-template]');
  const removeTemplate = root.querySelector('[data-header-search-remove-template]');
  const resultTemplate = root.querySelector('[data-header-search-result-template]');
  const predictiveTemplate = root.querySelector('[data-header-search-predictive-template]');
  const recentStatus = root.querySelector('[data-header-search-recent-status]');
  const results = root.querySelector('[data-header-search-results]');
  const searchStatus = root.querySelector('[data-header-search-status]');
  const requestStatus = root.querySelector('[data-header-search-request-status]');
  const browseSection = root.querySelector('[data-header-search-browse]');
  const browseHeading = root.querySelector('[data-header-search-browse-heading]');
  const browseLinks = Array.from(root.querySelectorAll('[data-header-search-browse-link]'));
  const shopAll = root.querySelector('[data-header-search-shop-all]');
  const shopAllLabel = root.querySelector('[data-header-search-shop-all-label]');
  const searchHelpOptionGroups = Array.from(
    root.querySelectorAll('[data-header-search-help-option]'),
  );
  const placeholderTemplate = root.querySelector('[data-header-search-placeholder-template]');
  const searchField = input?.closest('[data-input-clear]');
  const placeholderLayer = searchField?.querySelector('[data-input-clear-placeholder]');
  const placeholderPhrases = placeholderTemplate
    ? Array.from(
        placeholderTemplate.content.querySelectorAll('[data-header-search-placeholder]'),
        (placeholder) => placeholder.textContent.trim(),
      ).filter(Boolean)
    : [];
  const compactPlaceholder = input?.dataset.headerSearchCompactPlaceholder?.trim();
  const searchIndexUrl = form?.dataset.headerSearchIndexUrl || '';
  const allProductsUrl = form?.dataset.headerSearchAllProductsUrl || '/collections/all';
  let availableRoasters = [];
  const roasterSourceUrl = headerInner?.querySelector('[data-roaster-source-url]')?.dataset.roasterSourceUrl || '';

  if (
    !desktopTrigger ||
    !triggers.length ||
    !form ||
    !backdrop ||
    !surface ||
    !input ||
    !closeButton ||
    !panel ||
    !content ||
    !persistentSection ||
    !persistentHeading ||
    !recentSection ||
    !recentList ||
    !historyTemplate ||
    !removeTemplate ||
    !resultTemplate ||
    !recentStatus ||
    !results ||
    !searchStatus ||
    !requestStatus ||
    !browseSection ||
    !browseHeading ||
    !browseLinks.length ||
    !shopAll ||
    !shopAllLabel ||
    searchHelpOptionGroups.length !== 2 ||
    !headerInner
  ) {
    return;
  }

  root.dataset.headerSearchReady = 'true';
  const abortController = new AbortController();
  const { signal } = abortController;
  triggers.forEach((trigger) => {
    trigger.setAttribute('aria-keyshortcuts', '/');
    trigger.setAttribute('title', 'Search (/)');
  });

  document.addEventListener('keydown', (event) => {
    if (!matchesShortcut(event, '/', { modifier: false }) || root.dataset.searchOpen === 'true') return;
    const trigger = desktopSearchViewport.matches ? desktopTrigger : compactTrigger;
    if (!trigger || trigger.closest('[inert]')) return;
    event.preventDefault();
    openSearch({ keyboardFocus: true, sourceTrigger: trigger });
  }, { signal });
  let closeTimer = 0;
  let requestTimer = 0;
  let viewportFrame = 0;
  let placeholderTimer = 0;
  let placeholderCycle = 0;
  let placeholderIndex = 0;
  let placeholderAnimation = null;
  let placeholderAnimationTarget = null;
  let activeRequest = null;
  let pressedSearchResult = null;
  let pendingSearchUpdates = [];
  let searchRequestVersion = 0;
  let resultPressTimer = 0;
  let resizeAnimation = null;
  let activeTrigger = desktopTrigger;
  let renderedRecentSearchSignature = '';
  let resumeRequestOnPageShow = false;
  const inertedElements = new Set();

  const invalidateSearchUpdates = () => {
    searchRequestVersion += 1;
    pendingSearchUpdates = [];
    pressedSearchResult = null;
    window.clearTimeout(resultPressTimer);
    resultPressTimer = 0;
  };

  const resetPlaceholderAnimationTarget = () => {
    if (!placeholderAnimationTarget) return;

    if (
      placeholderAnimationTarget === placeholderLayer &&
      searchField?.classList.contains('is-placeholder-resting')
    ) {
      placeholderAnimationTarget.style.opacity = '1';
      placeholderAnimationTarget.style.transform = 'translate3d(0, 0, 0)';
      placeholderAnimationTarget.style.filter = 'blur(0)';
    } else {
      placeholderAnimationTarget.style.removeProperty('opacity');
      placeholderAnimationTarget.style.removeProperty('transform');
      placeholderAnimationTarget.style.removeProperty('filter');
    }
    placeholderAnimationTarget = null;
  };

  const setSearchPlaceholder = (index) => {
    const placeholder =
      index === 0 && !desktopSearchViewport.matches && compactPlaceholder
        ? compactPlaceholder
        : placeholderPhrases[index];
    if (!placeholder) return;

    input.placeholder = placeholder;
    if (
      placeholderLayer &&
      searchField?.matches('.is-placeholder-entering, .is-placeholder-resting')
    ) {
      placeholderLayer.textContent = placeholder;
    }
  };

  const stopSearchPlaceholderRotation = ({ reset = false } = {}) => {
    window.clearTimeout(placeholderTimer);
    placeholderTimer = 0;
    placeholderCycle += 1;
    placeholderAnimation?.cancel();
    placeholderAnimation = null;
    resetPlaceholderAnimationTarget();

    if (reset) {
      placeholderIndex = 0;
      setSearchPlaceholder(placeholderIndex);
    }
  };

  const canRotateSearchPlaceholder = () =>
    root.dataset.searchOpen === 'true' &&
    input.value.length === 0 &&
    !reduceSearchMotion.matches &&
    document.visibilityState !== 'hidden' &&
    placeholderPhrases.length > 1 &&
    typeof input.animate === 'function';

  const scheduleSearchPlaceholderRotation = (
    delay = SEARCH_PLACEHOLDER_HOLD_DURATION,
  ) => {
    window.clearTimeout(placeholderTimer);
    placeholderTimer = 0;
    if (!canRotateSearchPlaceholder()) return;

    placeholderTimer = window.setTimeout(rotateSearchPlaceholder, delay);
  };

  const rotateSearchPlaceholder = async () => {
    placeholderTimer = 0;
    if (!canRotateSearchPlaceholder()) return;

    const cycle = ++placeholderCycle;
    const target = searchField?.classList.contains('is-placeholder-resting')
      ? placeholderLayer
      : input;
    if (!target?.animate) return;

    placeholderAnimationTarget = target;
    placeholderAnimation = target.animate(
      [
        { opacity: 1, transform: 'translate3d(0, 0, 0)', filter: 'blur(0)' },
        { opacity: 0, transform: 'translate3d(0, -6px, 0)', filter: 'blur(2px)' },
      ],
      {
        duration: SEARCH_PLACEHOLDER_EXIT_DURATION,
        easing: SEARCH_PLACEHOLDER_EXIT_EASING,
        fill: 'forwards',
      },
    );

    try {
      await placeholderAnimation.finished;
    } catch (error) {
      return;
    }
    if (cycle !== placeholderCycle || !canRotateSearchPlaceholder()) return;

    placeholderAnimation.cancel();
    placeholderIndex = (placeholderIndex + 1) % placeholderPhrases.length;
    setSearchPlaceholder(placeholderIndex);
    placeholderAnimation = target.animate(
      [
        { opacity: 0, transform: 'translate3d(0, 6px, 0)', filter: 'blur(2px)' },
        { opacity: 1, transform: 'translate3d(0, 0, 0)', filter: 'blur(0)' },
      ],
      {
        duration: SEARCH_PLACEHOLDER_ENTER_DURATION,
        easing: SEARCH_PLACEHOLDER_ENTER_EASING,
        fill: 'forwards',
      },
    );

    try {
      await placeholderAnimation.finished;
    } catch (error) {
      return;
    }
    if (cycle !== placeholderCycle) return;

    placeholderAnimation.cancel();
    placeholderAnimation = null;
    resetPlaceholderAnimationTarget();
    scheduleSearchPlaceholderRotation();
  };

  setSearchPlaceholder(placeholderIndex);

  const setRequestLoading = (isLoading, { refining = false } = {}) => {
    // Initial matches remain readable to assistive technology during enrichment.
    results.setAttribute('aria-busy', String(isLoading && !refining));
    results.toggleAttribute('data-search-refining', isLoading && refining);
    requestStatus.hidden = !isLoading;
    requestStatus.setAttribute('aria-hidden', String(!isLoading));
    requestStatus.querySelector('.button__loading-text').textContent = refining
      ? 'Refining matches…'
      : 'Searching products';
  };

  const readSearchHistoryState = () => {
    const searchState = window.history.state?.[SEARCH_HISTORY_STATE_KEY];
    if (!searchState || typeof searchState !== 'object') return null;

    return {
      query: typeof searchState.query === 'string' ? searchState.query : '',
      scrollTop: Number.isFinite(searchState.scrollTop) ? Math.max(0, searchState.scrollTop) : 0,
    };
  };

  const writeSearchHistoryState = () => {
    if (root.dataset.searchOpen !== 'true') return;

    try {
      window.history.replaceState(
        {
          ...(window.history.state || {}),
          [SEARCH_HISTORY_STATE_KEY]: {
            query: input.value,
            scrollTop: content.scrollTop,
          },
        },
        '',
      );
    } catch (error) {}
  };

  const clearSearchHistoryState = () => {
    if (!window.history.state?.[SEARCH_HISTORY_STATE_KEY]) return;

    try {
      const nextState = { ...window.history.state };
      delete nextState[SEARCH_HISTORY_STATE_KEY];
      window.history.replaceState(nextState, '');
    } catch (error) {}
  };

  const getOptions = () =>
    Array.from(root.querySelectorAll('[data-header-search-option]')).filter(
      (option) => option instanceof HTMLElement && option.offsetParent !== null,
    );

  const syncSearchHelp = () => {
    const hasOptions = getOptions().length > 0;
    searchHelpOptionGroups.forEach((group) => {
      group.hidden = !hasOptions;
    });
  };

  const setPersistentHeading = (isRecovery) => {
    persistentSection.toggleAttribute('data-search-recovery', isRecovery);
    persistentHeading.textContent = isRecovery
      ? 'Try instead'
      : persistentHeading.dataset.defaultLabel || 'Suggested searches';
  };

  const setPageInert = (isInert) => {
    if (!isInert) {
      inertedElements.forEach((element) => element.removeAttribute('inert'));
      inertedElements.clear();
      return;
    }

    const siteHeader = root.closest('.site-header');
    const candidates = [
      ...Array.from(document.body.children).filter((element) => !element.contains(root)),
      ...(siteHeader
        ? Array.from(
            siteHeader.querySelectorAll(
              '.site-announcement, .site-header__desktop-nav, .site-header__logo, .site-header__actions',
            ),
          )
        : []),
    ];

    candidates.forEach((element) => {
      if (!(element instanceof HTMLElement) || element.hasAttribute('inert')) return;
      element.setAttribute('inert', '');
      inertedElements.add(element);
    });
  };

  const getDialogFocusables = () =>
    Array.from(
      form.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter(
      (element) =>
        element instanceof HTMLElement &&
        !element.hidden &&
        element.offsetParent !== null &&
        !element.closest('[inert]'),
    );

  const updateBrowseSuggestions = (searchTerm) => {
    const normalizedTerm = normalizeSearchText(searchTerm);
    const matchingLinks = browseLinks.filter((link) => {
      const keywords = normalizeSearchText(link.dataset.searchKeywords).split(' ').filter(Boolean);
      return keywords.some(
        (keyword) => keyword.startsWith(normalizedTerm) || normalizedTerm.startsWith(keyword),
      );
    });
    const visibleLinks = matchingLinks.slice(0, 5);
    const visibleLinkSet = new Set(visibleLinks);

    browseLinks.forEach((link) => {
      link.hidden = !visibleLinkSet.has(link);
    });
    browseSection.hidden = visibleLinks.length === 0;
    browseHeading.textContent = visibleLinks.length > 1 ? 'Related categories' : 'Related category';
  };

  const renderRecentSearches = () => {
    const searches = readRecentSearches().slice(0, SEARCH_HISTORY_LIMIT);
    const searchSignature = JSON.stringify(searches);

    if (
      searchSignature === renderedRecentSearchSignature &&
      recentList.childElementCount === searches.length
    ) {
      recentSection.hidden = searches.length === 0;
      return;
    }

    renderedRecentSearchSignature = searchSignature;
    recentList.replaceChildren();

    searches.forEach((searchTerm) => {
      const item = document.createElement('div');
      const link = document.createElement('a');
      const text = document.createElement('span');
      const removeButton = document.createElement('button');

      item.className = 'site-header__search-recent-item';
      const searchUrl = new URL(form.action, window.location.origin);
      searchUrl.searchParams.set('q', searchTerm);
      searchUrl.searchParams.set('type', 'product');
      searchUrl.searchParams.set('options[unavailable_products]', 'last');
      link.href = `${searchUrl.pathname}${searchUrl.search}`;
      link.dataset.headerSearchOption = '';
      link.dataset.headerSearchTerm = searchTerm;
      text.textContent = searchTerm;
      link.append(historyTemplate.content.cloneNode(true), text);

      removeButton.className = 'site-header__search-recent-remove';
      removeButton.type = 'button';
      removeButton.dataset.headerSearchRecentRemove = '';
      removeButton.dataset.headerSearchTerm = searchTerm;
      removeButton.setAttribute('aria-label', `Remove ${searchTerm} from recent searches`);
      removeButton.append(removeTemplate.content.cloneNode(true));

      item.append(link, removeButton);
      recentList.append(item);
    });

    recentSection.hidden = searches.length === 0;
  };

  const resetPanelViewportConstraint = () => {
    panel.style.removeProperty('max-height');
  };

  const keepFocusedOptionVisible = () => {
    const focusedElement = document.activeElement;
    if (!(focusedElement instanceof HTMLElement) || !content.contains(focusedElement)) return;

    const contentBounds = content.getBoundingClientRect();
    const focusedBounds = focusedElement.getBoundingClientRect();
    const edgePadding = 12;

    if (focusedBounds.bottom > contentBounds.bottom - edgePadding) {
      content.scrollTop += focusedBounds.bottom - contentBounds.bottom + edgePadding;
    } else if (focusedBounds.top < contentBounds.top + edgePadding) {
      content.scrollTop -= contentBounds.top - focusedBounds.top + edgePadding;
    }
  };

  const scheduleViewportSync = () => {
    window.cancelAnimationFrame(viewportFrame);
    viewportFrame = window.requestAnimationFrame(() => {
      viewportFrame = 0;
      if (root.dataset.searchOpen !== 'true') return;

      resetPanelViewportConstraint();
    });
  };

  const syncOpenLayoutMode = (isDesktopSearch) => {
    if (placeholderIndex === 0) setSearchPlaceholder(placeholderIndex);
    if (root.dataset.searchOpen !== 'true') return;

    root.setAttribute('data-search-instant', '');
    activeTrigger = isDesktopSearch ? desktopTrigger : compactTrigger;

    resetPanelViewportConstraint();

    window.requestAnimationFrame(() => {
      root.removeAttribute('data-search-instant');
    });
  };

  const finishPanelResize = () => {
    resizeAnimation?.cancel();
    resizeAnimation = null;
    surface.style.removeProperty('height');
  };

  const updatePanelContent = (update) => {
    if (
      !desktopSearchViewport.matches ||
      reduceSearchMotion.matches ||
      root.dataset.searchOpen !== 'true' ||
      typeof surface.animate !== 'function'
    ) {
      finishPanelResize();
      update();
      return;
    }

    const startHeight = surface.getBoundingClientRect().height;
    resizeAnimation?.cancel();
    resizeAnimation = null;
    surface.style.removeProperty('height');
    update();
    const endHeight = surface.getBoundingClientRect().height;

    if (Math.abs(endHeight - startHeight) < 1) return;

    surface.style.height = `${startHeight}px`;
    surface.getBoundingClientRect();
    resizeAnimation = surface.animate(
      [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
      {
        duration: SEARCH_RESIZE_DURATION,
        easing: SEARCH_RESIZE_EASING,
      },
    );

    const activeResizeAnimation = resizeAnimation;
    activeResizeAnimation.finished
      .catch(() => {})
      .finally(() => {
        if (resizeAnimation !== activeResizeAnimation) return;
        resizeAnimation = null;
        surface.style.removeProperty('height');
      });
  };

  const resetPredictiveSearch = ({ animate = true } = {}) => {
    window.clearTimeout(requestTimer);
    requestTimer = 0;
    activeRequest?.abort();
    activeRequest = null;
    invalidateSearchUpdates();
    const reset = () => {
      results.hidden = true;
      results.replaceChildren();
      setRequestLoading(false);
      shopAll.hidden = true;
      browseSection.hidden = true;
      persistentSection.hidden = false;
      setPersistentHeading(false);
      searchStatus.textContent = '';
      renderRecentSearches();
      syncSearchHelp();
    };

    if (animate) {
      updatePanelContent(reset);
    } else {
      reset();
    }
  };

  const animatePredictiveResults = () => {
    if (reduceSearchMotion.matches || document.visibilityState === 'hidden' || !results.animate) {
      return;
    }

    results.getAnimations?.().forEach((animation) => animation.cancel());
    results.animate(
      [
        { opacity: 0, transform: 'translate3d(0, 4px, 0)' },
        { opacity: 1, transform: 'translate3d(0, 0, 0)' },
      ],
      {
        duration: 160,
        easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
        fill: 'backwards',
      },
    );
  };

  const getProductResultKey = (url) => {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/$/, '');
    } catch (error) {
      return url;
    }
  };

  const getNativeProductRecord = (item) => ({
    url: item.getAttribute('href') || '',
    available:
      item.querySelector('[data-predictive-search-stock]')?.dataset.stockState !== 'sold-out',
    search: {
      title: item.querySelector('[data-predictive-search-title]')?.textContent,
      tasteNotes: item.querySelector('[data-predictive-search-notes]')?.textContent,
      varieties: [],
      process: [],
      other: item.querySelector('[data-predictive-search-vendor]')?.textContent,
    },
  });

  const renderIndexedProductResult = (product) => {
    const item = resultTemplate.content.firstElementChild.cloneNode(true);
    const imageContainer = item.querySelector('[data-predictive-search-result-image]');
    const vendor = item.querySelector('[data-predictive-search-vendor]');
    const title = item.querySelector('[data-predictive-search-title]');
    const notes = item.querySelector('[data-predictive-search-notes]');
    const size = item.querySelector('[data-predictive-search-size]');
    const price = item.querySelector('[data-predictive-search-price]');
    const stock = item.querySelector('[data-predictive-search-stock]');
    const stockLabel = item.querySelector('[data-predictive-search-stock-label]');
    const stockCompact = item.querySelector('[data-predictive-search-stock-compact]');
    const noteValues = (Array.isArray(product.notes) ? product.notes.flat(Infinity) : [product.notes])
      .filter((value) => value !== null && value !== undefined && String(value).trim())
      .slice(0, 3);

    item.href = product.url;
    vendor.textContent = product.vendor || '';
    title.textContent = product.title || '';
    price.textContent = product.price || '';

    if (product.image?.url) {
      const image = document.createElement('img');
      image.src = product.image.url;
      image.alt = product.image.alt || '';
      image.width = 75;
      image.height = 75;
      image.loading = 'eager';
      imageContainer.append(image);
    }

    if (noteValues.length) {
      notes.textContent = noteValues.join(' · ');
      notes.hidden = false;
    }

    if (product.size) {
      size.textContent = product.size;
      size.hidden = false;
    }

    if (product.available === false) {
      stockLabel.textContent = 'Sold out';
      stockCompact.textContent = 'Sold out';
      stock.setAttribute('aria-label', 'Sold out');
      stock.dataset.stockState = 'sold-out';
      stock.hidden = false;
    } else if (product.lowStock) {
      stockLabel.textContent = `Only ${product.inventoryQuantity} left!`;
      stockCompact.textContent = `${product.inventoryQuantity} left`;
      stock.setAttribute('aria-label', `Only ${product.inventoryQuantity} left`);
      stock.dataset.stockState = 'low-stock';
      stock.hidden = false;
    }

    return item;
  };

  const renderVendorMatches = (predictiveSearch, searchTerm, indexedProducts = [], roasterDirectory = []) => {
    const list = predictiveSearch.querySelector('[data-predictive-search-vendor-list]');
    if (!list) return 0;

    const vendors = new Map();
    const comingSoonVendors = new Set();
    const addVendor = (name, destination, details = {}) => {
      const label = String(name || '').trim();
      const key = normalizeSearchText(label);
      if (!key || !destination) return;

      const existing = vendors.get(key);
      if (!existing || (details.isRoaster && (!existing.isRoaster || details.image)) || (!existing.isRoaster && details.image && !existing.image)) {
        vendors.set(key, { name: label, url: destination, ...details });
      }
    };
    const vendorUrl = (name) => {
      const url = new URL(allProductsUrl, window.location.origin);
      url.searchParams.set('filter.p.vendor', name);
      url.searchParams.set('filter.v.availability', '1');
      return `${url.pathname}${url.search}`;
    };

    headerInner.querySelectorAll('[data-roaster-name]').forEach((link) => {
      if (link.getAttribute('aria-disabled') === 'true') {
        comingSoonVendors.add(normalizeSearchText(link.querySelector('.roaster-menu__name')?.textContent));
        return;
      }
      addVendor(link.querySelector('.roaster-menu__name')?.textContent, link.getAttribute('href'), { isRoaster: true });
    });
    roasterDirectory.forEach((roaster) => {
      if (roaster?.comingSoon === true) {
        comingSoonVendors.add(normalizeSearchText(roaster.name));
        return;
      }
      addVendor(roaster?.name, roaster?.url, {
        isRoaster: true,
        image: roaster?.image,
        country: roaster?.country,
        flag: roaster?.flag,
      });
    });
    predictiveSearch.querySelectorAll('[data-predictive-search-item]:not([data-predictive-search-vendor-item])').forEach((item) => {
      const vendorLabel = item.querySelector('[data-predictive-search-vendor]');
      const name = vendorLabel?.textContent?.trim();
      if (name) addVendor(name, vendorUrl(name), { image: vendorLabel.dataset.predictiveSearchVendorImage });
    });
    indexedProducts.forEach((product) => {
      const name = String(product?.vendor || '').trim();
      if (name) addVendor(name, vendorUrl(name));
    });

    const matches = [...vendors.values()]
      .map((vendor) => ({ ...vendor, quality: getSearchMatchQuality(vendor.name, searchTerm) }))
      .filter((vendor) => Number.isFinite(vendor.quality) && !comingSoonVendors.has(normalizeSearchText(vendor.name)))
      .sort((first, second) =>
        first.quality - second.quality || first.name.localeCompare(second.name),
      )
      .slice(0, 2);

    list.replaceChildren(...matches.map((vendor) => {
      const item = resultTemplate.content.firstElementChild.cloneNode(true);
      const imageContainer = item.querySelector('[data-predictive-search-result-image]');
      const label = item.querySelector('[data-predictive-search-vendor]');
      const title = item.querySelector('[data-predictive-search-title]');
      const description = item.querySelector('[data-predictive-search-notes]');
      const meta = item.querySelector('[data-predictive-search-meta]');
      const detail = item.querySelector('[data-predictive-search-price]');

      item.href = vendor.url;
      if (normalizeSearchText(vendor.name) === 'gemilai') {
        const landingUrl = new URL(allProductsUrl, window.location.origin);
        landingUrl.pathname = landingUrl.pathname.replace(/\/collections\/[^/]+\/?$/, '/collections/gemilai');
        landingUrl.search = '';
        item.href = landingUrl.pathname;
      }
      item.dataset.predictiveSearchVendorItem = '';
      item.dataset.headerSearchTerm = vendor.name;
      item.removeAttribute('data-header-search-result');
      imageContainer.setAttribute('aria-hidden', 'true');
      label.textContent = vendor.isRoaster ? 'Roaster' : 'Brand';
      title.textContent = vendor.name;
      description.textContent = [vendor.flag, vendor.country].filter(Boolean).join(' ');
      description.hidden = !description.textContent;
      detail.textContent = '';
      meta.hidden = true;
      loadAvailableVendorCount(vendor.url).then((count) => {
        if (count === null || !item.isConnected) return;
        detail.textContent = `${count} available ${count === 1 ? 'product' : 'products'}`;
        meta.hidden = false;
      });
      if (vendor.image) {
        const image = document.createElement('img');
        image.src = vendor.image;
        image.alt = '';
        image.width = 75;
        image.height = 75;
        image.loading = 'eager';
        imageContainer.append(image);
      } else {
        imageContainer.textContent = vendor.name.charAt(0).toLocaleUpperCase();
      }
      return item;
    }));
    list.hidden = matches.length === 0;
    return matches.length;
  };

  const applyMetafieldSearchRanking = (predictiveSearch, indexedProducts, searchTerm, focusPin) => {
    const nativeItems = Array.from(
      predictiveSearch.querySelectorAll('[data-predictive-search-item]:not([data-predictive-search-vendor-item])'),
    );
    const nativeItemsByKey = new Map(
      nativeItems.map((item) => [getProductResultKey(item.getAttribute('href') || ''), item]),
    );
    if (focusPin?.item) {
      nativeItemsByKey.set(getProductResultKey(focusPin.item.getAttribute('href') || ''), focusPin.item);
    }
    const candidates = [];
    const seenProducts = new Set();
    const indexedProductKeys = new Set();

    indexedProducts.forEach((product, index) => {
      const rank = getSearchMatchRank(product, searchTerm);
      const key = getProductResultKey(product.url || '');
      if (key) indexedProductKeys.add(key);
      if (!Number.isFinite(rank) || !key || seenProducts.has(key)) return;

      seenProducts.add(key);
      candidates.push({
        element: nativeItemsByKey.get(key) || null,
        index,
        product,
        rank,
      });
    });

    nativeItems.forEach((item, index) => {
      const nativeProduct = getNativeProductRecord(item);
      const key = getProductResultKey(nativeProduct.url);
      // A complete coffee record is more precise than a description-only native match.
      if (!key || seenProducts.has(key) || indexedProductKeys.has(key)) return;

      const nativeRank = getSearchMatchRank(nativeProduct, searchTerm);
      seenProducts.add(key);
      candidates.push({
        element: item,
        index: indexedProducts.length + index,
        product: nativeProduct,
        rank: Number.isFinite(nativeRank) ? nativeRank : 49,
      });
    });

    candidates.sort(
      (first, second) =>
        Number(first.product.available === false) - Number(second.product.available === false) ||
        first.rank - second.rank ||
        first.index - second.index,
    );

    let list = predictiveSearch.querySelector('[data-predictive-search-list]');
    const emptyState = predictiveSearch.querySelector('[data-predictive-search-empty]');
    const emptyHeading = predictiveSearch.querySelector('[data-predictive-search-empty-heading]');
    const querySuggestions = predictiveSearch.querySelector('[data-predictive-search-queries]');
    const count = predictiveSearch.querySelector('[data-predictive-search-count]');

    if (!list) {
      list = document.createElement('div');
      list.dataset.predictiveSearchList = '';
      predictiveSearch.insertBefore(list, emptyState || querySuggestions || null);
    }

    const focusedItem = focusPin?.item || nativeItems.find((item) => item.contains(document.activeElement));
    const focusedCandidate = candidates.find((candidate) => candidate.element === focusedItem);
    const visibleCandidates = candidates.slice(0, getSearchResultLimit());
    // Keep an option under keyboard focus in its current slot during refinement.
    if (focusedCandidate) {
      const index = visibleCandidates.indexOf(focusedCandidate);
      if (index !== -1) visibleCandidates.splice(index, 1);
      visibleCandidates.splice(Math.min(focusPin?.index ?? nativeItems.indexOf(focusedItem), getSearchResultLimit() - 1), 0, focusedCandidate);
      visibleCandidates.length = Math.min(visibleCandidates.length, getSearchResultLimit());
    }
    list.replaceChildren(
      ...visibleCandidates.map(
        (candidate) => candidate.element || renderIndexedProductResult(candidate.product),
      ),
    );
    list.hidden = visibleCandidates.length === 0;

    const hasResults = candidates.length > 0;
    if (emptyState) emptyState.hidden = hasResults;
    if (emptyHeading) emptyHeading.textContent = `No matches for “${searchTerm}”`;
    if (count) count.hidden = !hasResults;

    return candidates.length;
  };

  const syncSearchOutcome = (predictiveSearch, resultCount, searchTerm, { refining = false, incomplete = false } = {}) => {
    predictiveSearch
      .querySelectorAll('a[data-predictive-search-item]:not([data-predictive-search-vendor-item])')
      .forEach((link) => {
        link.dataset.sigmaPurchaseSource = 'search';
        link.dataset.sigmaSearchTerm = searchTerm;
      });

    const count = predictiveSearch.querySelector('[data-predictive-search-count]');
    const emptyState = predictiveSearch.querySelector('[data-predictive-search-empty]');
    const emptyHeading = predictiveSearch.querySelector('[data-predictive-search-empty-heading]');
    const hasQuerySuggestions = Boolean(
      predictiveSearch.querySelector('[data-predictive-search-query]'),
    );
    const vendorCount = predictiveSearch.querySelectorAll('[data-predictive-search-vendor-item]').length;
    const hasResults = resultCount > 0 || vendorCount > 0;
    const showPersistentRecovery = !refining && !hasResults && !hasQuerySuggestions;

    if (count) {
      count.hidden = !hasResults && !refining;
      count.textContent = incomplete ? 'Initial matches' : 'Top matches';
    }
    if (emptyState) emptyState.hidden = hasResults || refining;
    if (emptyHeading) emptyHeading.textContent = incomplete
      ? 'More matches are temporarily unavailable'
      : `No matches for “${searchTerm}”`;
    persistentSection.hidden = !showPersistentRecovery;
    setPersistentHeading(showPersistentRecovery);
    shopAll.hidden = resultCount === 0;
    shopAllLabel.textContent = formatAllResultsLabel(resultCount, searchTerm);
    highlightSearchMatches(predictiveSearch, searchTerm);
    syncSearchHelp();
    const visibleProductCount = Math.min(resultCount, getSearchResultLimit());
    const matchSummary = [
      vendorCount > 0 && `${vendorCount} brand or roaster ${vendorCount === 1 ? 'match' : 'matches'}`,
      visibleProductCount > 0 && `${visibleProductCount} product ${visibleProductCount === 1 ? 'match' : 'matches'}`,
    ].filter(Boolean).join(' and ');
    searchStatus.textContent = refining ? '' : incomplete
      ? `${hasResults ? `${matchSummary} available. ` : ''}Refined matches are temporarily unavailable. Press Enter to search.`
      : hasResults ? `${matchSummary} available.` : `No matches for ${searchTerm}. Try a broader term.`;
  };

  const updateSearchMatches = (update) => {
    if (pressedSearchResult) {
      pendingSearchUpdates.push(update);
      return;
    }
    const focusedOption = getOptions().find((option) => option.contains(document.activeElement));
    const focusedUrl = focusedOption?.getAttribute('href');
    const scrollTop = content.scrollTop;
    updatePanelContent(update);
    if (focusedUrl) {
      const option = getOptions().find((candidate) => candidate.getAttribute('href') === focusedUrl);
      (option || input).focus({ preventScroll: true });
      content.scrollTop = scrollTop;
    }
  };

  results.addEventListener('pointerdown', (event) => {
    pressedSearchResult = event.target.closest('[data-header-search-option]');
  }, { signal });
  const finishResultPress = () => {
    if (!pressedSearchResult && !pendingSearchUpdates.length) return;
    // Let the original click activate before a newly ranked row can move into its place.
    window.clearTimeout(resultPressTimer);
    resultPressTimer = window.setTimeout(() => {
      resultPressTimer = 0;
      pressedSearchResult = null;
      const updates = pendingSearchUpdates;
      pendingSearchUpdates = [];
      updates.forEach(updateSearchMatches);
    }, 0);
  };
  document.addEventListener('pointerup', finishResultPress, { signal });
  document.addEventListener('pointercancel', finishResultPress, { signal });

  const requestPredictiveSearch = () => {
    invalidateSearchUpdates();
    const requestVersion = searchRequestVersion;
    window.clearTimeout(requestTimer);
    requestTimer = 0;
    activeRequest?.abort();
    activeRequest = null;

    const searchTerm = input.value.trim();
    if (!searchTerm) {
      resetPredictiveSearch();
      return;
    }
    if (searchTerm.length < SEARCH_MINIMUM_LENGTH) {
      resetPredictiveSearch();
      searchStatus.textContent = 'Type one more character to search.';
      return;
    }

    availableRoasters
      .filter((roaster) => !roaster.comingSoon && roaster.image && Number.isFinite(getSearchMatchQuality(roaster.name, searchTerm)))
      .sort((a, b) => getSearchMatchQuality(a.name, searchTerm) - getSearchMatchQuality(b.name, searchTerm))
      .slice(0, 2)
      .forEach((roaster) => {
        const image = new Image();
        image.src = roaster.image;
      });

    const allResultsUrl = new URL(form.action, window.location.origin);
    allResultsUrl.searchParams.set('q', searchTerm);
    allResultsUrl.searchParams.set('type', 'product');
    allResultsUrl.searchParams.set('options[unavailable_products]', 'last');
    shopAll.href = `${allResultsUrl.pathname}${allResultsUrl.search}`;
    updateBrowseSuggestions(searchTerm);
    syncSearchHelp();
    setRequestLoading(true);

    requestTimer = window.setTimeout(async () => {
      requestTimer = 0;
      const requestController = new AbortController();
      activeRequest = requestController;
      let indexedProducts = getCachedProductSearchIndex(searchIndexUrl);
      let indexFinished = indexedProducts !== undefined;
      const indexPromise = loadProductSearchIndex(searchIndexUrl).then((products) => {
        indexedProducts = products;
        indexFinished = true;
        return products;
      }, () => {
        indexedProducts = null;
        indexFinished = true;
        return null;
      });
      const roasterPromise = loadRoasterDirectory(roasterSourceUrl).catch(() => null);
      const isCurrentRequest = () => !signal.aborted && !requestController.signal.aborted &&
        requestVersion === searchRequestVersion && root.dataset.searchOpen === 'true' &&
        input.value.trim() === searchTerm;
      let predictiveSearch;
      let resultCount = 0;
      const renderCachedMatches = ({ incomplete = false } = {}) => {
        if (!indexedProducts || !predictiveTemplate) return false;
        const cachedSearch = predictiveTemplate.content.firstElementChild.cloneNode(true);
        const cachedCount = applyMetafieldSearchRanking(cachedSearch, indexedProducts, searchTerm);
        if (!cachedCount) return false;
        predictiveSearch = cachedSearch;
        resultCount = cachedCount;
        updatePanelContent(() => {
          persistentSection.hidden = true;
          recentSection.hidden = true;
          results.hidden = false;
          results.replaceChildren(predictiveSearch);
          renderVendorMatches(predictiveSearch, searchTerm, indexedProducts, availableRoasters);
          setRequestLoading(!incomplete, { refining: !incomplete });
          syncSearchOutcome(predictiveSearch, resultCount, searchTerm, { refining: !incomplete, incomplete });
        });
        return true;
      };

      try {
        renderCachedMatches();
        const localeRoot = window.Shopify?.routes?.root || '/';
        const url = new URL(`${localeRoot}search/suggest`, window.location.origin);
        url.searchParams.set('q', searchTerm);
        url.searchParams.set('resources[type]', 'product,query');
        url.searchParams.set('resources[limit]', '5');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('section_id', 'predictive-search');

        const response = await fetch(url, {
          headers: { Accept: 'text/html' },
          signal: requestController.signal,
        });

        if (!response.ok) throw new Error(`Predictive search failed with ${response.status}`);

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nativeSearch = documentFragment.querySelector('[data-predictive-search]');
        if (!nativeSearch) throw new Error('Predictive search response is missing its results');

        if (!isCurrentRequest()) return;

        updateSearchMatches(() => {
          if (!isCurrentRequest()) return;
          const previousItems = Array.from(results.querySelectorAll('[data-predictive-search-item]:not([data-predictive-search-vendor-item])'));
          const focusedItem = previousItems.find((item) => item.contains(document.activeElement));
          const focusPin = focusedItem ? { item: focusedItem, index: previousItems.indexOf(focusedItem) } : undefined;
          predictiveSearch = nativeSearch;
          persistentSection.hidden = true;
          recentSection.hidden = true;
          results.hidden = false;
          resultCount = indexedProducts
            ? applyMetafieldSearchRanking(predictiveSearch, indexedProducts, searchTerm, focusPin)
            : predictiveSearch.querySelectorAll('[data-predictive-search-item]:not([data-predictive-search-vendor-item])').length;
          results.replaceChildren(predictiveSearch);
          renderVendorMatches(predictiveSearch, searchTerm, indexedProducts || [], availableRoasters);
          setRequestLoading(!indexFinished, { refining: !indexFinished });
          syncSearchOutcome(predictiveSearch, resultCount, searchTerm, {
            refining: !indexFinished,
            incomplete: indexFinished && indexedProducts === null,
          });
        });
        animatePredictiveResults();

        if (!indexFinished) {
          await indexPromise;
          if (!isCurrentRequest()) return;
          updateSearchMatches(() => {
            if (!isCurrentRequest()) return;
            resultCount = indexedProducts
              ? applyMetafieldSearchRanking(predictiveSearch, indexedProducts, searchTerm)
              : resultCount;
            renderVendorMatches(predictiveSearch, searchTerm, indexedProducts || [], availableRoasters);
            setRequestLoading(false);
            syncSearchOutcome(predictiveSearch, resultCount, searchTerm, { incomplete: indexedProducts === null });
          });
        }

        // Optional roaster enrichment must not delay accurate product results.
        const roasterDirectory = await roasterPromise;
        if (!isCurrentRequest() || !roasterDirectory) return;
        availableRoasters = roasterDirectory;
        updateSearchMatches(() => {
          if (!isCurrentRequest()) return;
          renderVendorMatches(predictiveSearch, searchTerm, indexedProducts || [], roasterDirectory);
          syncSearchOutcome(predictiveSearch, resultCount, searchTerm, { incomplete: indexedProducts === null });
        });
      } catch (error) {
        if (!isCurrentRequest()) return;

        await indexPromise;
        if (!isCurrentRequest()) return;
        if (predictiveSearch) {
          updateSearchMatches(() => {
            if (!isCurrentRequest()) return;
            setRequestLoading(false);
            syncSearchOutcome(predictiveSearch, resultCount, searchTerm, { incomplete: true });
          });
          return;
        }
        if (renderCachedMatches({ incomplete: true })) return;

        updatePanelContent(() => {
          persistentSection.hidden = true;
          recentSection.hidden = true;
          results.hidden = false;
          results.innerHTML =
            '<p data-predictive-search-status>Search suggestions are unavailable. Press Enter to search.</p>';
          setRequestLoading(false);
          shopAll.hidden = false;
          shopAllLabel.textContent = `Search all products for “${searchTerm}”`;
          syncSearchHelp();
          searchStatus.textContent = 'Search suggestions are unavailable. Press Enter to search.';
        });
      } finally {
        if (activeRequest === requestController) activeRequest = null;
      }
    }, SEARCH_REQUEST_DELAY);
  };

  const closeSearch = ({ restoreFocus = true, instant = false } = {}) => {
    if (root.dataset.searchOpen !== 'true') return;

    invalidateSearchUpdates();

    stopSearchPlaceholderRotation({ reset: true });
    finishPanelResize();
    window.clearTimeout(closeTimer);
    window.clearTimeout(requestTimer);
    requestTimer = 0;
    window.cancelAnimationFrame(viewportFrame);
    viewportFrame = 0;
    activeRequest?.abort();
    activeRequest = null;
    root.toggleAttribute('data-search-instant', instant || reduceSearchMotion.matches);
    clearSearchHistoryState();
    root.dataset.searchClosing = 'true';
    delete root.dataset.searchOpen;
    delete root.dataset.searchKeyboardFocus;
    triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    panel.setAttribute('inert', '');
    setPageInert(false);

    if (restoreFocus) {
      activeTrigger.focus({ preventScroll: true });
    } else if (form.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    const finishClose = () => {
      if (root.dataset.searchOpen === 'true') return;
      form.hidden = true;
      delete root.dataset.searchLayout;
      resetPanelViewportConstraint();
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      resetPredictiveSearch({ animate: false });

      if (instant || reduceSearchMotion.matches) {
        root.removeAttribute('data-search-instant');
        delete root.dataset.searchClosing;
        return;
      }

      delete root.dataset.searchClosing;
    };

    if (instant || reduceSearchMotion.matches) {
      finishClose();
    } else {
      closeTimer = window.setTimeout(finishClose, SEARCH_MOTION.closeDuration);
    }
  };

  const openSearch = ({
    instant = false,
    keyboardFocus = false,
    sourceTrigger = desktopTrigger,
  } = {}) => {
    if (root.dataset.searchOpen === 'true') return;

    // Share the catalogue load across opening, typing, and subsequent searches.
    loadProductSearchIndex(searchIndexUrl).catch(() => {});
    loadRoasterDirectory(roasterSourceUrl).then((roasters) => {
      availableRoasters = roasters;
    }).catch(() => {});

    stopSearchPlaceholderRotation({ reset: true });
    window.clearTimeout(closeTimer);
    delete root.dataset.searchClosing;
    document
      .querySelectorAll('[data-header-search][data-search-open="true"]')
      .forEach((otherRoot) => otherRoot !== root && otherRoot.dispatchEvent(new CustomEvent('header-search:close')));
    document.querySelectorAll('[data-desktop-disclosure][open]').forEach((menu) => menu.removeAttribute('open'));

    const isDesktopSearch = desktopSearchViewport.matches;
    activeTrigger = sourceTrigger;
    root.dataset.searchLayout = 'true';
    resetPanelViewportConstraint();
    form.hidden = false;
    renderRecentSearches();
    const skipEntryMotion = instant || reduceSearchMotion.matches || keyboardFocus;
    root.toggleAttribute('data-search-instant', skipEntryMotion);
    if (keyboardFocus) {
      root.dataset.searchKeyboardFocus = 'true';
    } else {
      delete root.dataset.searchKeyboardFocus;
    }
    triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    setPageInert(true);

    if (!skipEntryMotion) {
      surface.getBoundingClientRect();
    }

    requestAnimationFrame(() => {
      root.dataset.searchOpen = 'true';
      panel.removeAttribute('inert');
      input.focus({ preventScroll: true });
      syncSearchHelp();
      scheduleSearchPlaceholderRotation(SEARCH_PLACEHOLDER_INITIAL_DELAY);

      requestAnimationFrame(() => root.removeAttribute('data-search-instant'));
    });
  };

  const moveOptionFocus = (direction) => {
    const options = getOptions();
    if (!options.length) return;

    const activeIndex = options.indexOf(document.activeElement);
    const nextIndex =
      activeIndex === -1
        ? direction > 0
          ? 0
          : options.length - 1
        : (activeIndex + direction + options.length) % options.length;

    options[nextIndex].focus({ preventScroll: true });
    keepFocusedOptionVisible();
  };

  triggers.forEach((trigger) => {
    trigger.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        openSearch({
          keyboardFocus: event.detail === 0 && event.isTrusted,
          sourceTrigger: trigger,
        });
      },
      { signal },
    );
  });

  closeButton.addEventListener('click', () => closeSearch(), { signal });
  backdrop.addEventListener('click', () => closeSearch(), { signal });
  root.addEventListener('header-search:close', () => closeSearch({ restoreFocus: false }), { signal });

  form.addEventListener('submit', () => writeRecentSearch(input.value), { signal });
  form.addEventListener('click', (event) => {
    const removeButton = event.target.closest('[data-header-search-recent-remove]');
    if (removeButton) {
      event.preventDefault();

      const removeButtons = Array.from(
        recentList.querySelectorAll('[data-header-search-recent-remove]'),
      );
      const removeIndex = removeButtons.indexOf(removeButton);
      const searchTerm = removeButton.dataset.headerSearchTerm || '';

      removeRecentSearch(searchTerm);
      updatePanelContent(renderRecentSearches);
      recentStatus.textContent = `Removed ${searchTerm} from recent searches.`;

      const remainingButtons = Array.from(
        recentList.querySelectorAll('[data-header-search-recent-remove]'),
      );
      const nextButton = remainingButtons[Math.min(removeIndex, remainingButtons.length - 1)];
      (nextButton || input).focus({ preventScroll: true });
      return;
    }

    const option = event.target.closest('[data-header-search-option]');
    if (!option) return;

    if (option.matches('[data-header-search-result], [data-header-search-shop-all]')) {
      writeRecentSearch(input.value);
    } else {
      writeRecentSearch(option.dataset.headerSearchTerm || option.textContent);
    }
  }, { signal });
  input.addEventListener('input', () => {
    requestPredictiveSearch();

    if (input.value.length > 0) {
      stopSearchPlaceholderRotation();
      return;
    }

    scheduleSearchPlaceholderRotation(
      searchField?.classList.contains('is-clearing')
        ? SEARCH_PLACEHOLDER_CLEAR_DELAY
        : SEARCH_PLACEHOLDER_HOLD_DURATION,
    );
  }, { signal });

  window.addEventListener('storage', (event) => {
    if (
      event.key !== SEARCH_STORAGE_KEY ||
      root.dataset.searchOpen !== 'true' ||
      input.value.trim()
    ) {
      return;
    }

    renderRecentSearches();
  }, { signal });

  window.addEventListener('pagehide', () => {
    invalidateSearchUpdates();
    stopSearchPlaceholderRotation();
    writeSearchHistoryState();
    resumeRequestOnPageShow =
      root.dataset.searchOpen === 'true' &&
      input.value.trim().length >= SEARCH_MINIMUM_LENGTH &&
      (requestTimer !== 0 || results.getAttribute('aria-busy') === 'true' || results.hasAttribute('data-search-refining'));

    window.clearTimeout(requestTimer);
    requestTimer = 0;
    activeRequest?.abort();
    activeRequest = null;
    results.getAnimations?.().forEach((animation) => animation.cancel());
    setRequestLoading(false);
  }, { signal });

  window.addEventListener('pageshow', () => {
    if (root.dataset.searchOpen !== 'true') {
      renderRecentSearches();
      return;
    }

    form.hidden = false;
    panel.removeAttribute('inert');
    setPageInert(true);
    triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    syncOpenLayoutMode(desktopSearchViewport.matches);
    scheduleSearchPlaceholderRotation(SEARCH_PLACEHOLDER_INITIAL_DELAY);

    if (resumeRequestOnPageShow) {
      resumeRequestOnPageShow = false;
      requestPredictiveSearch();
    }
  }, { signal });

  root.addEventListener('keydown', (event) => {
    if (root.dataset.searchOpen !== 'true') return;

    if (event.key === 'Tab' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      root.dataset.searchKeyboardFocus = 'true';
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeSearch();
      return;
    }

    if (event.key === 'Tab') {
      const focusables = getDialogFocusables();
      const firstFocusable = focusables[0];
      const lastFocusable = focusables.at(-1);

      if (
        focusables.length &&
        ((event.shiftKey && document.activeElement === firstFocusable) ||
          (!event.shiftKey && document.activeElement === lastFocusable) ||
          !form.contains(document.activeElement))
      ) {
        event.preventDefault();
        (event.shiftKey ? lastFocusable : firstFocusable).focus({ preventScroll: true });
      }
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      moveOptionFocus(event.key === 'ArrowDown' ? 1 : -1);
    }
  }, { signal });

  document.addEventListener('pointerdown', (event) => {
    if (root.contains(event.target)) delete root.dataset.searchKeyboardFocus;

    if (root.dataset.searchOpen === 'true' && !root.contains(event.target)) {
      closeSearch({ restoreFocus: false });
    }
  }, { signal });

  desktopSearchViewport.addEventListener(
    'change',
    (event) => syncOpenLayoutMode(event.matches),
    { signal },
  );
  reduceSearchMotion.addEventListener(
    'change',
    (event) => {
      stopSearchPlaceholderRotation({ reset: true });
      if (!event.matches) scheduleSearchPlaceholderRotation();
    },
    { signal },
  );
  document.addEventListener(
    'visibilitychange',
    () => {
      if (document.visibilityState === 'hidden') {
        stopSearchPlaceholderRotation();
      } else {
        scheduleSearchPlaceholderRotation();
      }
    },
    { signal },
  );
  window.addEventListener('resize', scheduleViewportSync, { signal });
  window.addEventListener('orientationchange', scheduleViewportSync, { signal });
  window.visualViewport?.addEventListener('resize', scheduleViewportSync, { signal });
  window.visualViewport?.addEventListener('scroll', scheduleViewportSync, { signal });

  const savedSearchState = readSearchHistoryState();
  if (savedSearchState) {
    input.value = savedSearchState.query;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    openSearch({
      instant: true,
      sourceTrigger: desktopSearchViewport.matches ? desktopTrigger : compactTrigger,
    });
    window.requestAnimationFrame(() => {
      content.scrollTop = savedSearchState.scrollTop;
    });
  }

  headerSearchControllers.set(root, {
    destroy() {
      invalidateSearchUpdates();
      stopSearchPlaceholderRotation({ reset: true });
      abortController.abort();
      window.clearTimeout(closeTimer);
      window.clearTimeout(requestTimer);
      window.cancelAnimationFrame(viewportFrame);
      activeRequest?.abort();
      finishPanelResize();
      results.getAnimations?.().forEach((animation) => animation.cancel());
      resetPanelViewportConstraint();
      clearSearchHistoryState();
      setPageInert(false);
      setRequestLoading(false);
      triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      delete root.dataset.headerSearchReady;
    },
  });
};

const initializeHeaderSearches = (root = document) => {
  root.querySelectorAll('[data-header-search]').forEach(initializeHeaderSearch);
};

initializeHeaderSearches();
document.addEventListener('shopify:section:load', (event) => initializeHeaderSearches(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  headerSearchControllers.forEach((controller, root) => {
    if (!event.target.contains(root)) return;

    controller.destroy();
    headerSearchControllers.delete(root);
  });
});
